"use client"

import { useId } from "react"

interface WaveDividerProps {
  /** Position of wave relative to section */
  position?: 'top' | 'bottom'
  /** Orientation of wave */
  orientation?: 'normal' | 'reverse'
  /** Color matching brand palette */
  color?: 'white' | 'grey' | 'blue' | 'sky-gradient'
  /** Custom height in pixels (default: 140px, mobile: 90px) */
  height?: number
  /** Apply standardized section margin (80px) */
  alignWithSection?: boolean
  /** Add gradient overlay for smoother transitions (default: true for gradients) */
  overlayGradient?: boolean
  /** Additional CSS classes */
  className?: string
}

// Brand color mappings - using existing design tokens and gradient colors
const colorMap: Record<string, string> = {
  'white': '#ffffff',
  'grey': '#f8fafc',      // slate-50 (gradient end)
  'light-grey': '#f0f4f8', // DEPRECATED: Use sky-gradient instead
  'blue': '#dbeafe',      // blue-100
  'dark': '#444446',      // dark grey for footer transitions
  'section-blue': '#DFE9F4', // Primary section background
  'section-fade': '#F5F8FB', // Fade-to-white section end
}

// Gradient definitions - Sky Gradient Fade matches hero blue palette
const gradientMap: Record<string, { id: string; stops: Array<{ offset: string; color: string }> }> = {
  'sky-gradient': {
    id: 'sky-gradient-wave',
    stops: [
      { offset: '0%', color: '#B8CCDF' },   // Brand Blue
      { offset: '100%', color: '#DFE9F4' }  // Matches Section Background
    ]
  }
}

/**
 * WaveDivider component for creating consistent SVG wave transitions between sections
 *
 * Follows standardized specifications:
 * - Wave height: 140px desktop, 90px mobile
 * - SVG viewBox: 0 0 1440 140
 * - Consistent curvature and amplitude across all instances
 * - 80px section padding alignment
 * - Full-width responsive scaling
 *
 * @param {WaveDividerProps} props - Component props
 * @returns {JSX.Element} Rendered wave divider
 *
 * @example
 * ```tsx
 * <WaveDivider position="bottom" color="white" alignWithSection={true} />
 * ```
 */
export default function WaveDivider({
  position = 'bottom',
  orientation = 'normal',
  color = 'white',
  height,
  alignWithSection = true,
  overlayGradient,
  className = '',
}: WaveDividerProps) {
  const uniqueId = useId()
  
  // Determine if using gradient or solid color
  const gradient = gradientMap[color]
  // Create a unique ID for this instance's gradient to avoid conflicts when multiple waves exist
  const gradientId = gradient ? `${gradient.id}-${uniqueId.replace(/:/g, '')}` : undefined
  
  const fillColor = gradient && gradientId ? `url(#${gradientId})` : (colorMap[color] || colorMap.white)

  // Auto-enable overlay for gradient waves unless explicitly disabled
  const showOverlay = overlayGradient !== undefined ? overlayGradient : !!gradient

  // Standardized single wave path for consistency across all dividers
  // Smooth, professional curve matching reference design
  // Amplitude: ~100px (baseline y=50, peaks y=110, valleys y=10) - ENHANCED for prominence
  const wavePath = 'M0,50 C360,110 480,10 720,50 C960,110 1080,10 1440,50 L1440,140 L0,140 Z'

  // Determine if wave should be flipped
  const shouldFlip = orientation === 'reverse'

  // Responsive heights following specification
  const desktopHeight = height || 140
  const mobileHeight = height ? Math.floor(height * 0.64) : 90 // Mobile: ~64% of desktop

  // Vertical spacing for section alignment (80px standard)
  const verticalSpacing = alignWithSection ? '80px' : '0'
  const marginTop = position === 'bottom' ? verticalSpacing : '0'
  const marginBottom = position === 'top' ? verticalSpacing : '0'

  return (
    <div
      className={`wave-wrapper relative w-full overflow-hidden ${className}`}
      style={{
        marginTop,
        marginBottom,
        lineHeight: 0, // Prevents gaps below SVG
        boxShadow: gradient ? '0 -2px 10px rgba(107, 155, 210, 0.18)' : 'none', // Enhanced separation glow for gradient waves
      }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 1440 140"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        className={`absolute w-full ${shouldFlip ? 'scale-y-[-1]' : ''}`}
        style={{
          height: `${desktopHeight}px`,
          filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.08))',
        }}
      >
        {/* Gradient definitions for smooth color transitions */}
        {gradient && gradientId && (
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
              {gradient.stops.map((stop, idx) => (
                <stop key={idx} offset={stop.offset} stopColor={stop.color} />
              ))}
            </linearGradient>
          </defs>
        )}

        <path
          d={wavePath}
          fill={fillColor}
        />
      </svg>

      {/* Spacer div to maintain height - responsive */}
      <div
        className="block md:hidden"
        style={{ height: `${mobileHeight}px` }}
      />
      <div
        className="hidden md:block"
        style={{ height: `${desktopHeight}px` }}
      />

      {/* Gradient overlay bridge - creates smooth transition into section below */}
      {showOverlay && (
        <div
          className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, transparent, rgba(184,204,223,0.04))',
            transform: 'translateY(100%)',
            zIndex: -1
          }}
          aria-hidden="true"
        />
      )}
    </div>
  )
}
